import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, signOut, type User, connectAuthEmulator } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ArchivedApplicationsPage from './pages/ArchivedApplicationsPage';
import ProfileSettingsPage from './pages/ProfileSettings/ProfileSettingsPage';
import ResumeMatchingPage from './pages/ResumeMatchingPage';
import ApplicationDetailsPage from './pages/ApplicationDetailsPage';
import ExtensionAuthHandler from './components/ExtensionAuthHandler';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyByKgPAfuSbxEaN4QPF4Rl94aaXVmgOMcw",
  authDomain: "application-tracker-7dd9b.firebaseapp.com",
  projectId: "application-tracker-7dd9b",
  storageBucket: "application-tracker-7dd9b.firebasestorage.app",
  messagingSenderId: "535738103644",
  appId: "1:535738103644:web:3ccfdd8ff37166a041b399",
  measurementId: "G-7XGT0TWY8M"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Connect to emulators in development
if (import.meta.env.DEV) {
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  } catch (error) {
    // Emulators already connected
  }
}

// Export for use in other components
export { db, storage };

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for logout action from extension
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'logout') {
      signOut(auth).then(() => {
        // Clear the URL params
        window.history.replaceState({}, '', '/login');
      });
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setLoading(false);

      // Sync auth state with extension if it's installed
      const extensionId = localStorage.getItem('extensionId');
      if (extensionId && window.chrome?.runtime?.sendMessage) {
        try {
          if (user) {
            // User logged in - send credentials to extension
            const idToken = await user.getIdToken();
            const refreshToken = (user as any).stsTokenManager?.refreshToken;

            window.chrome.runtime.sendMessage(
              extensionId,
              {
                type: 'AUTH_FROM_WEB_APP',
                credentials: {
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName,
                  photoURL: user.photoURL,
                  idToken,
                  refreshToken,
                },
              },
              (response) => {
                if (!response?.success) {
                  console.warn('Failed to sync auth with extension:', response?.error);
                }
              }
            );
          } else {
            // User logged out - notify extension
            window.chrome.runtime.sendMessage(
              extensionId,
              { type: 'LOGOUT_FROM_WEB_APP' },
              (response) => {
                if (!response?.success) {
                  console.warn('Failed to sync logout with extension:', response?.error);
                }
              }
            );
          }
        } catch (err) {
          console.warn('Error syncing auth with extension:', err);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <ThemeProvider>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  // Check if coming from extension
  const isFromExtension = window.location.search.includes('source=extension');

  return (
    <ThemeProvider>
      <Routes>
        <Route
          path="/login"
          element={
            user ? (
              isFromExtension ? <ExtensionAuthHandler user={user} /> : <Navigate to="/" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/"
          element={user ? <DashboardPage user={user} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/archived"
          element={user ? <ArchivedApplicationsPage user={user} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/settings"
          element={user ? <ProfileSettingsPage user={user} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/resume-matcher"
          element={user ? <ResumeMatchingPage user={user} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/applications/:id"
          element={user ? <ApplicationDetailsPage user={user} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="*"
          element={<Navigate to="/" replace />}
        />
      </Routes>
    </ThemeProvider>
  );
}
